import { useEffect, useState, useCallback } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { absUrl } from "@/lib/config";
import {
  DoorOpen, CheckCircle, XCircle, UploadSimple, DownloadSimple, Trash,
  FloppyDisk, Warning, Package, Star, Rocket,
} from "@phosphor-icons/react";

function RatingInput({ value, onChange, readOnly, dataTestid }) {
  const rating = Number(value) || 0;
  return (
    <div className="flex items-center gap-1" data-testid={dataTestid}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => !readOnly && onChange(n)}
          className={`w-8 h-8 flex items-center justify-center border ${rating >= n ? "border-[#F59E0B] bg-[#F59E0B]/10" : "border-border"} ${readOnly ? "" : "hover:bg-muted"}`}
        >
          <Star size={16} weight={rating >= n ? "fill" : "regular"} className={rating >= n ? "text-[#F59E0B]" : "text-muted-foreground"} />
        </button>
      ))}
      <span className="text-[10px] font-mono-mini text-muted-foreground ml-2">{rating}/5</span>
    </div>
  );
}

export default function Offboarding() {
  const { user } = useAuth();
  const { can } = useCapabilities();
  const [rec, setRec] = useState(null);
  const [cfg, setCfg] = useState({ exit_questions: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const [assetReturn, setAssetReturn] = useState({});
  const [handoverNotes, setHandoverNotes] = useState("");
  const [exitInterview, setExitInterview] = useState({});
  const [resignationFileId, setResignationFileId] = useState(null);
  const [resignationMeta, setResignationMeta] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/offboarding/me"),
        api.get("/offboarding/config"),
      ]);
      setRec(r.data);
      setCfg(c.data || { exit_questions: [] });
      if (r.data) {
        setAssetReturn(r.data.asset_return || {});
        setHandoverNotes(r.data.handover_notes || "");
        setExitInterview(r.data.exit_interview || {});
        setResignationFileId(r.data.resignation_letter_file_id || null);
        setResignationMeta(r.data.resignation_letter_meta || null);
      }
    } catch (e) {
      setMsg({ type: "error", text: formatAxiosError(e) });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/offboarding/${user.id}`, {
        asset_return: assetReturn,
        handover_notes: handoverNotes,
        exit_interview: exitInterview,
        resignation_letter_file_id: resignationFileId,
      });
      setRec(r.data);
      flash("ok", "Saved.");
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setSaving(false); }
  };

  const uploadResignation = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResignationFileId(r.data.id);
      setResignationMeta({ id: r.data.id, filename: file.name, length: file.size, content_type: file.type });
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setUploading(false); }
  };

  if (loading) return <div className="p-12 text-sm text-muted-foreground">Loading…</div>;

  if (!rec) {
    return (
      <div className="p-12 max-w-2xl mx-auto text-center">
        <div className="eyebrow flex items-center gap-2 justify-center mb-2"><DoorOpen size={12} weight="bold" /> Offboarding</div>
        <h1 className="font-display text-4xl font-black tracking-tight mb-3">No active offboarding</h1>
        <p className="text-sm text-muted-foreground">
          Your offboarding hasn&apos;t been initiated. If you&apos;re planning to leave, please contact your
          manager or HR to start the process.
        </p>
      </div>
    );
  }

  const isComplete = rec.status === "complete";

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-8">
        <div className="eyebrow flex items-center gap-2 mb-2"><DoorOpen size={12} weight="bold" /> Offboarding</div>
        <h1 className="font-display text-4xl font-black tracking-tight">Your offboarding checklist</h1>
        <div className="mt-3 text-sm text-muted-foreground">
          Last working day: <span className="font-semibold text-foreground">{rec.last_working_day}</span>
          {rec.reason && <span> • Reason: <span className="font-mono-mini uppercase text-[10px]">{rec.reason}</span></span>}
          {isComplete && <span className="ml-2 bg-[#22C55E] text-white text-[10px] font-mono-mini px-1.5 py-0.5">COMPLETE</span>}
        </div>
      </div>

      <div className="p-8 lg:p-12 max-w-4xl space-y-8">
        {msg.text && (
          <div className={`px-4 py-2 text-sm border flex items-center gap-2 ${msg.type === "ok" ? "border-[#22C55E] bg-green-50 text-[#166534]" : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"}`}>
            {msg.type === "ok" ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
            {msg.text}
          </div>
        )}

        {/* Asset return */}
        <section className="border border-border p-6">
          <div className="eyebrow flex items-center gap-2 mb-3">
            <Package size={12} weight="bold" /> Return company assets
          </div>
          <h2 className="font-display text-2xl font-black tracking-tight mb-3">Assets checklist</h2>
          {rec.assets?.length === 0 ? (
            <p className="text-sm text-muted-foreground">No assets are currently assigned to you.</p>
          ) : (
            <div className="space-y-2">
              {rec.assets.map(a => (
                <div key={a.id} className="border border-border p-3 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-sm font-semibold">{a.name}</div>
                    <div className="text-[10px] font-mono-mini text-muted-foreground">{a.category} • {a.serial || "no serial"}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    {["returned", "pending", "lost", "na"].map(s => {
                      const active = (assetReturn[a.id] || "pending") === s;
                      const colors = { returned: "bg-[#22C55E] text-white", pending: "bg-[#F59E0B] text-white", lost: "bg-[#FF3B30] text-white", na: "bg-muted text-foreground" };
                      return (
                        <button
                          key={s}
                          disabled={isComplete}
                          onClick={() => setAssetReturn({ ...assetReturn, [a.id]: s })}
                          className={`text-[10px] font-mono-mini px-2 py-1 uppercase ${active ? colors[s] : "border border-border hover:bg-muted"}`}
                          data-testid={`asset-${a.id}-${s}`}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Handover notes */}
        <section className="border border-border p-6">
          <div className="eyebrow mb-3">Handover notes</div>
          <textarea
            value={handoverNotes}
            onChange={e => setHandoverNotes(e.target.value)}
            rows={6}
            readOnly={isComplete}
            placeholder="Summary of ongoing work, key contacts, credentials to transfer, current status of your projects, etc."
            className="w-full border border-border px-3 py-2 text-sm"
            data-testid="handover-notes"
          />
        </section>

        {/* Exit interview */}
        <section className="border border-border p-6">
          <div className="eyebrow mb-3">Exit interview</div>
          <div className="space-y-4">
            {(cfg.exit_questions || []).map(q => (
              <div key={q.key} data-testid={`exit-q-${q.key}`}>
                <label className="text-sm font-semibold block mb-2">{q.label}</label>
                {q.type === "text" && (
                  <textarea
                    value={exitInterview[q.key] || ""}
                    onChange={e => setExitInterview({ ...exitInterview, [q.key]: e.target.value })}
                    rows={3}
                    readOnly={isComplete}
                    className="w-full border border-border px-3 py-2 text-sm"
                  />
                )}
                {q.type === "rating" && (
                  <RatingInput
                    value={exitInterview[q.key]}
                    onChange={(n) => setExitInterview({ ...exitInterview, [q.key]: n })}
                    readOnly={isComplete}
                    dataTestid={`rating-${q.key}`}
                  />
                )}
                {q.type === "select" && (
                  <select
                    value={exitInterview[q.key] || ""}
                    disabled={isComplete}
                    onChange={e => setExitInterview({ ...exitInterview, [q.key]: e.target.value })}
                    className="w-full border border-border px-3 py-2 text-sm"
                  >
                    <option value="">— choose —</option>
                    {(q.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Resignation letter */}
        <section className="border border-border p-6">
          <div className="eyebrow mb-3">Resignation letter (optional)</div>
          {resignationMeta || resignationFileId ? (
            <div className="border border-border p-3 flex items-center justify-between">
              <span className="text-sm font-semibold">{resignationMeta?.filename || "letter.pdf"}</span>
              <div className="flex items-center gap-2">
                <a href={absUrl(`/api/files/${resignationFileId}`)} target="_blank" rel="noreferrer" className="border border-border px-2 py-1 text-xs hover:bg-muted flex items-center gap-1">
                  <DownloadSimple size={12} weight="bold" /> View
                </a>
                {!isComplete && (
                  <button onClick={() => { setResignationFileId(null); setResignationMeta(null); }} className="border border-border p-1 hover:bg-muted">
                    <Trash size={12} weight="bold" />
                  </button>
                )}
              </div>
            </div>
          ) : !isComplete && (
            <label className="border border-dashed border-border p-4 flex items-center justify-center gap-2 text-sm cursor-pointer hover:bg-muted">
              <UploadSimple size={14} weight="bold" />
              {uploading ? "Uploading…" : "Upload resignation letter"}
              <input type="file" accept="application/pdf,image/*" className="hidden"
                onChange={e => uploadResignation(e.target.files?.[0])}
                data-testid="resignation-upload" />
            </label>
          )}
        </section>

        {!isComplete && (
          <div className="flex items-center justify-end">
            <button
              onClick={save}
              disabled={saving}
              className="btn-primary flex items-center gap-2 disabled:opacity-40"
              data-testid="offb-save"
            >
              <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save progress"}
            </button>
          </div>
        )}

        {isComplete && (
          <div className="border border-[#22C55E] bg-green-50 text-[#166534] p-4 text-sm flex items-center gap-2">
            <CheckCircle size={16} weight="fill" />
            Your offboarding was marked complete on {rec.completed_at?.slice(0, 10)}. Thank you for your contributions.
          </div>
        )}
      </div>
    </div>
  );
}
